import { useEffect } from 'react';

import { AppFrame } from './AppFrame';
import { DevAudio } from './DevAudio';
import { Kit } from '../surfaces/Kit/Kit';
import { useUiStore } from './uiStore';
import { adapterFor } from '../core/speech/setup';
import { setActiveAdapter } from '../core/input/activeAdapter';
import type { InputModeId } from '../core/input/InputAdapter';
import { activate } from '../core/input/activationRegistry';
import { CareEntryCorner } from './CareEntryCorner';
import { useProfileStore } from '../core/profile/ProfileStore';
import { BetreuerModus } from '../surfaces/BetreuerModus/BetreuerModus';
import { PartnerAssisted } from '../surfaces/PartnerAssisted/PartnerAssisted';
import { Sprechen } from '../surfaces/Sprechen/Sprechen';
import { Saetze } from '../surfaces/Saetze/Saetze';
import { Notfall } from '../surfaces/Notfall/Notfall';
import { PartnerAnsicht } from '../surfaces/PartnerAnsicht/PartnerAnsicht';

/**
 * Deliberate-throw route, kept in production builds on purpose.
 *
 * INV-01 says to prove the boundary by throwing, and the proof that matters is on the
 * real iPad in the installed PWA — which is a production build. It needs an exact query string that
 * `start_url` never produces, so the person cannot reach it: `?crash=render`.
 */
function CrashOnPurpose(): never {
  throw new Error('deliberate throw: ?crash=render');
}

/**
 * Binds the active `InputAdapter` to the document.
 *
 * The adapter — not each component — decides what counts as an activation: release within target,
 * slop tolerance, tremor debounce, offset correction. It then hands the target id to the activation
 * registry, which knows what that target means.
 *
 * That indirection is the whole point. Surfaces never listen for clicks, so Phase 3 can add dwell
 * and scanning — neither of which produces a click — without touching a single surface (§5.1).
 */
function useInputAdapter(inputMode: InputModeId): void {
  useEffect(() => {
    // Phase 3's first real test of Phase 1's bet: switching modes is choosing a different adapter, and
    // nothing else in the app changes. Two taps in care mode, no reload — which is one of the phase's
    // acceptance criteria and is satisfied by this effect's dependency rather than by any new code.
    const adapter = adapterFor(inputMode);

    setActiveAdapter(adapter);
    adapter.attach(document.documentElement, (intent) => {
      if (intent.kind === 'activate') activate(intent.targetId);
    });

    return () => {
      adapter.detach();
      setActiveAdapter(null);
    };
  }, [inputMode]);
}

export function App(): React.JSX.Element {
  const surface = useUiStore((s) => s.surface);
  const goTo = useUiStore((s) => s.goTo);
  const inputMode = useProfileStore((s) => s.settings.inputMode);
  useInputAdapter(inputMode);

  const params = new URLSearchParams(window.location.search);
  if (params.get('crash') === 'render') return <CrashOnPurpose />;
  // Device harness for the audio checks that could not wait for the surfaces. Removed when
  // Betreuer-Modus diagnostics land in 1.13.
  if (params.get('dev') === 'audio') return <DevAudio />;
  // The no-tech kit (1.16). A route rather than a care-mode screen because its output is paper: a
  // caregiver opens it, prints five sheets, laminates them and puts them in the room.
  if (params.get('print') === 'kit') return <Kit />;

  // Betreuer-Modus is dense caregiver UI that scrolls, with its own tabs in the top right — an
  // invisible corner over them would eat a real control, and there is nothing to enter from there
  // anyway. Every other surface gets the corner.
  if (surface === 'betreuer') return <BetreuerModus onExit={() => goTo('sprechen')} />;

  // Two surfaces take over the whole screen rather than sitting inside the frame, each for its own
  // reason: Partner-Ansicht wants the largest possible text, and partner-assisted scanning needs two
  // half-screen targets. Partner-assisted is an input *mode*, not a destination — when it is selected
  // it replaces the typing surface entirely, because the person cannot use a keyboard on the days it
  // is for.
  const content =
    surface === 'partner' ? (
      <PartnerAnsicht />
    ) : inputMode === 'partnerAssisted' && surface === 'sprechen' ? (
      <PartnerAssisted onExit={() => goTo('saetze')} />
    ) : (
      <AppFrame>
        {surface === 'sprechen' ? <Sprechen /> : null}
        {surface === 'saetze' ? <Saetze /> : null}
        {surface === 'notfall' ? <Notfall /> : null}
      </AppFrame>
    );

  return (
    <>
      {/* Care-mode entry. Deliberately not routed through the input adapter: if it were, a caregiver
          who selected an input mode that does not work for this person would have no way back in to
          undo it. Outside `content` so it survives Partner-Ansicht's 180° rotation and sits above a
          content band the caregiver may have widened over the corner. */}
      <CareEntryCorner onEnter={() => goTo('betreuer')} />
      {content}
    </>
  );
}
